import { useCallback, useRef } from 'react';
import useCanvasStore from '../stores/canvasStore';

export const useDrag = (viewport, updateElement, selectElement, onDragMove, getCanvasElements) => {
  const currentDragTarget = useRef(null);
  const dragOffset = useRef(null);

  const dragMove = useCallback((event) => {
    if (currentDragTarget.current && viewport && dragOffset.current) {
      // Convert current mouse position to world coordinates
      const worldPosition = viewport.toWorld(event.global.x, event.global.y);

      // Apply the offset to maintain relative positioning
      currentDragTarget.current.x = worldPosition.x - dragOffset.current.x;
      currentDragTarget.current.y = worldPosition.y - dragOffset.current.y;

      // Sync interactive area
      if (currentDragTarget.current.interactiveArea) {
        currentDragTarget.current.interactiveArea.x = currentDragTarget.current.x;
        currentDragTarget.current.interactiveArea.y = currentDragTarget.current.y;
      }

      // Notify about drag move (for transform controls)
      if (onDragMove) {
        onDragMove(currentDragTarget.current);
      }
    }
  }, [viewport, onDragMove]);

  const dragEnd = useCallback(() => {
    if (currentDragTarget.current && viewport) {
      viewport.off('pointermove', dragMove);
      // currentDragTarget.current.alpha = 1;

      updateElement(currentDragTarget.current.elementId, {
        x: currentDragTarget.current.x,
        y: currentDragTarget.current.y
      });

      currentDragTarget.current = null;
      dragOffset.current = null;
    }
  }, [viewport, dragMove, updateElement]);

  const dragStart = useCallback((sprite, event) => {
    if (!viewport) return;

    // Validate sprite before using it
    if (!sprite || sprite.destroyed || sprite.scale === null) {
      console.warn('Invalid or destroyed sprite passed to dragStart');
      return;
    }

    // Check if sprite is still in the scene
    if (!sprite.parent) {
      console.warn('Sprite is no longer in the scene');
      return;
    }

    // End existing drag
    if (currentDragTarget.current) {
      viewport.off('pointermove', dragMove);
      // currentDragTarget.current.alpha = 1;
    }

    // Calculate offset between mouse position and sprite center
    const worldPosition = viewport.toWorld(event.global.x, event.global.y);
    dragOffset.current = {
      x: worldPosition.x - sprite.x,
      y: worldPosition.y - sprite.y
    };

    // Start new drag
    currentDragTarget.current = sprite;
    // sprite.alpha = 0.8;

    // Sync interactive area
    if (sprite.interactiveArea) {
      sprite.interactiveArea.x = sprite.x;
      sprite.interactiveArea.y = sprite.y;
    }

    // Select element from canvas context (preserves all updates)
    // Use store getState() for fresh reference - closure may be stale after media replacement
    const currentElements = useCanvasStore.getState().canvasElements;
    const canvasElement = currentElements?.find(el => el.id === sprite.elementId);
    if (canvasElement) {
      selectElement(canvasElement);
    } else {
      // Fallback to creating element data (for new elements)
      selectElement({
        id: sprite.elementId,
        type: 'video',
        name: sprite.componentData.title || sprite.componentData.name,
        x: sprite.x,
        y: sprite.y,
        width: sprite.width,
        height: sprite.height,
        componentData: sprite.componentData
      });
    }

    viewport.on('pointermove', dragMove);
  }, [viewport, dragMove, selectElement, getCanvasElements]);

  // Setup viewport handlers once
  if (viewport && !viewport._dragSetup) {
    viewport.eventMode = 'static';
    viewport.on('pointerup', dragEnd);
    viewport.on('pointerupoutside', dragEnd);
    viewport._dragSetup = true;
  }

  return { dragStart };
};